import * as echarts from 'echarts';
import React, { useEffect, useRef, useState } from 'react';
import { DASHBOARD_COLORS } from '../../../../constants';

interface CategoryData {
  label: string;
  value: number;
}

export interface TimeData {
  [key: string]: CategoryData[];
}

interface IPieCartWithStats {
  label: string;
  data: TimeData | undefined;
  loading?: boolean;
  colors?: string[];
  type?: 'donut' | 'column' | 'line' | 'bar';
  hideToggles?: boolean;
  maxValue?: number;
  legendWidth?: string;
  barWidthOverride?: string | number;
}

export const PieCartWithStats = ({
  label,
  data,
  loading = false,
  colors = DASHBOARD_COLORS,
  type = 'donut',
  hideToggles = false,
  maxValue,
  legendWidth = 'w-[32%]',
  barWidthOverride,
}: IPieCartWithStats) => {
  const [timeRange, setTimeRange] = useState<string>('Week');
  const chartRef = useRef<HTMLDivElement | null>(null);

  const selectedData = data?.[timeRange] ?? [];
  const totalValue = selectedData.reduce((sum, item) => sum + item.value, 0);

  // For Area charts to 'fill', they need at least 2 points.
  // If we have only 1 point, we add a leading zero point.
  const displayData =
    type === 'line' && selectedData.length === 1
      ? [{ label: '', value: 0 }, ...selectedData]
      : selectedData;

  const noDataMessages: Record<string, string> = {
    Week: 'No data for the past week.',
    Month: 'No data for the last month.',
    '6 Months': 'No insights for 6 months.',
  };

  useEffect(() => {
    if (!chartRef.current || loading || displayData.length === 0) return;

    const chart = echarts.init(chartRef.current);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => chart.resize())
        : null;

    let option: echarts.EChartsOption = {};

    const nonZeroItems = displayData.filter((item) => item.value > 0);

    const appleTooltipStyle = {
      backgroundColor: 'rgba(255, 255, 255, 0.96)',
      textStyle: {
        color: '#0f172a',
        fontSize: 12,
        fontWeight: 500,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
      },
      borderWidth: 0,
      padding: [8, 12],
      extraCssText:
        'backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); border-radius: 10px; box-shadow: 0 4px 20px -2px rgba(0,0,0,0.12), 0 2px 6px -1px rgba(0,0,0,0.06); border: 1px solid rgba(226, 232, 240, 0.8);',
    };

    if (type === 'donut') {
      option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: <span style="font-weight:700; color:#0f172a;">{c}</span> <span style="color:#64748b;">({d}%)</span>',
          ...appleTooltipStyle,
        },
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '40%',
            style: {
              text: totalValue.toString(),
              fill: '#0f172a',
              fontSize: 26,
              fontWeight: 'bold',
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
            },
          },
          {
            type: 'text',
            left: 'center',
            top: '58%',
            style: {
              text: 'TOTAL',
              fill: '#94a3b8',
              fontSize: 10,
              fontWeight: 'bold',
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
            },
          },
        ],
        series: [
          {
            name: label,
            type: 'pie',
            radius: ['68%', '86%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            padAngle: nonZeroItems.length > 1 ? 3 : 0,
            itemStyle: {
              borderRadius: nonZeroItems.length > 1 ? 6 : 0,
            },
            label: { show: false },
            emphasis: {
              scale: true,
              scaleSize: 4,
              label: { show: false },
            },
            data: displayData.map((item, index) => ({
              value: item.value,
              name: item.label,
              itemStyle: {
                color: colors[index % colors.length],
              },
            })),
          },
        ],
      };
    } else if (type === 'column') {
      option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any) => {
            const item = Array.isArray(params) ? params[0] : params;
            return `<div style="font-size:11px; color:#64748b;">${item.name}</div><div style="font-weight:700; font-size:13px; color:#0f172a; margin-top:2px;">${item.value}</div>`;
          },
          ...appleTooltipStyle,
        },
        grid: {
          left: '2%',
          right: '3%',
          bottom: '2%',
          top: '12%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: displayData.map((item) => item.label),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#e2e8f0' } },
          axisLabel: {
            color: '#64748b',
            fontSize: 11,
            fontWeight: 500,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
            rotate: 25,
          },
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 10,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
          },
          max: maxValue,
        },
        series: [
          {
            data: displayData.map((item, index) => ({
              value: item.value,
              itemStyle: { color: colors[index % colors.length] },
            })),
            type: 'bar',
            barWidth: barWidthOverride || 16,
            itemStyle: { borderRadius: [6, 6, 0, 0] },
          },
        ],
      };
    } else if (type === 'bar') {
      option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any) => {
            const item = Array.isArray(params) ? params[0] : params;
            return `<div style="font-size:11px; color:#64748b;">${item.name}</div><div style="font-weight:700; font-size:13px; color:#0f172a; margin-top:2px;">${item.value}</div>`;
          },
          ...appleTooltipStyle,
        },
        grid: {
          left: '2%',
          right: '8%',
          bottom: '4%',
          top: '6%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 10,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
          },
          max: maxValue,
        },
        yAxis: {
          type: 'category',
          data: displayData.map((item) => item.label),
          inverse: true,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#e2e8f0' } },
          axisLabel: {
            color: '#64748b',
            fontSize: 11,
            fontWeight: 500,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
          },
        },
        series: [
          {
            data: displayData.map((item, index) => ({
              value: item.value,
              itemStyle: { color: colors[index % colors.length] },
            })),
            type: 'bar',
            barWidth: barWidthOverride || 12,
            itemStyle: { borderRadius: [0, 6, 6, 0] },
          },
        ],
      };
    } else if (type === 'line') {
      option = {
        tooltip: {
          trigger: 'axis',
          formatter: (params: any) => {
            const item = Array.isArray(params) ? params[0] : params;
            return `<div style="font-size:11px; color:#64748b;">${item.name}</div><div style="font-weight:700; font-size:13px; color:#0f172a; margin-top:2px;">${item.value}</div>`;
          },
          ...appleTooltipStyle,
        },
        grid: {
          left: '4%',
          right: '6%',
          bottom: '10%',
          top: '12%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: displayData.map((item) => item.label),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#e2e8f0' } },
          axisLabel: {
            color: '#64748b',
            fontSize: 11,
            fontWeight: 500,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
            margin: 12,
          },
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 10,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
          },
        },
        series: [
          {
            data: displayData.map((item) => item.value),
            type: 'line',
            smooth: 0.4,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 7,
            lineStyle: {
              width: 3.5,
              color: colors[0],
              shadowBlur: 8,
              shadowColor: 'rgba(99, 102, 241, 0.2)',
            },
            areaStyle: {
              opacity: 0.25,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: colors[0] },
                { offset: 1, color: 'rgba(255, 255, 255, 0)' },
              ]),
            },
            itemStyle: {
              color: colors[0],
              borderColor: '#ffffff',
              borderWidth: 2,
            },
          },
        ],
      };
    }

    chart.setOption(option);

    const handleResize = () => chart.resize();
    resizeObserver?.observe(chartRef.current);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
      chart.dispose();
    };
  }, [displayData, label, colors, loading, totalValue, type]);

  return (
    <div
        className={`min-w-0 rounded-2xl border border-slate-200/70 bg-white p-4 sm:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.03),0_6px_16px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:border-slate-300/80 transition-all duration-300 h-[265px] sm:h-[285px] lg:h-[300px] flex flex-col justify-between ${
          loading ? 'animate-pulse' : ''
        }`}
    >
      { /* Header */ }
      <div className='flex min-w-0 items-center justify-between gap-3 mb-2 shrink-0'>
        <h2 className='text-[14px] font-semibold text-slate-900 tracking-tight'>
          { label }
        </h2>
        { !hideToggles && (
          <div className='flex shrink-0 bg-slate-100/90 p-1 rounded-xl border border-slate-200/50 backdrop-blur-md'>
            { ['Week', 'Month', '6 Months'].map((range) => (
              <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-all duration-200 ${
                    timeRange === range
                      ? 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] text-slate-900 font-semibold ring-1 ring-black/[0.04]'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
              >
                { range === '6 Months' ? '6M' : range }
              </button>
            )) }
          </div>
        ) }
      </div>

      { /* Body */ }
      { loading ? (
        <div className='h-full bg-slate-50/80 rounded-xl' />
      ) : selectedData.length > 0 ? (
        <div className='flex min-w-0 items-center gap-3 flex-1 min-h-0'>
          { /* Chart Container */ }
          <div ref={chartRef} className='min-w-0 flex-1 h-full' />

          { /* Legend Container */ }
          <div
              className={`hidden sm:flex flex-col justify-center border-l border-slate-100 relative pl-3 pr-1 gap-1.5 overflow-y-auto max-h-full ${legendWidth}`}
          >
            { selectedData.map((entry, index) => (
              <div
                  key={index}
                  className='flex items-center justify-between group py-0.5'
              >
                <div className='flex items-center gap-2 max-w-[75%]'>
                  <span
                      className='h-2.5 w-2.5 rounded-full shrink-0 ring-2 ring-slate-100/80 shadow-xs'
                      style={{ backgroundColor: colors[index % colors.length] }}
                  />
                  <span className='text-[11px] font-medium text-slate-600 truncate group-hover:text-slate-900 transition-colors'>
                    { entry.label }
                  </span>
                </div>
                <span className='text-[12px] font-semibold text-slate-800 tabular-nums shrink-0'>
                  { entry.value }
                </span>
              </div>
            )) }
          </div>
        </div>
      ) : (
        <div className='flex items-center justify-center h-full text-center text-[11px] text-slate-400 font-medium px-4'>
          { noDataMessages[timeRange] ?? 'No data to display.' }
        </div>
      ) }
    </div>
  );
};
